import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import prettyBytes from 'pretty-bytes';
import { useNavigate } from 'react-router';

import { Project } from '../../../app/entities/project';
import { tabPool } from '../../../features/tabs/models/tabs-pool';
import { timeDifference } from '../../../lib/utils/TimeDifference';
import { formatDate } from '../../../lib/utils/DateHelper';
import { ListViewItem } from '../../../components/list-view/list-view';
import { useResponsive } from '../../../hooks/useResponsive';
import { CoverImage } from '../../../components/project-item/CoverImage';
import { ProjectMenu } from '../../assets/components/project-menu';
import { PrioritySelector } from '../../process/components/priority-selector';
import { useCurrentMember } from '../../../hooks/use-current-member';
import { Collaborators } from '@producer/ui-kit';
import { openModal } from '../../../core/modal/open-modal';
import { ProjectCollaboratorModal } from '../modals/project-collaborator.modal';
import { Member } from '../../../app/entities/member';

import styles from './project-list-item.module.scss';
interface ProjectListItemProps {
  project: Project;
}

export const ProjectListItem: React.FC<ProjectListItemProps> = observer(({ project }) => {
  const navigate = useNavigate();
  const { isDesktop } = useResponsive();

  const currentMember = useCurrentMember();

  const archivedByMember = useMemo(() => {
    if (!project?.archivedById) return null;
    return Member.getOne(project.archivedById);
  }, [project?.archivedById]);

  const handleOpenProject = (e: React.MouseEvent) => {
    const href = `/space/${project.spaceId}/projects/${project._id}`;

    tabPool.newTab({
      key: project._id,
      label: project.name,
      href,
    });

    if (!e.shiftKey) {
      navigate(href);
    }
  };

  return (
    <ListViewItem
      className={styles['styled-list-view-item']}
      onClick={handleOpenProject}
      data-archived={!!project.archived}
    >
      <td className={styles['project-avatar-wrapper']}>
        <CoverImage key={project.avatar} src={project.avatar || ''} title={project.name} />
      </td>
      <td style={{ width: '100%' }}>
        {project.name}
        {project.archived && project.archivedAt && (
          <span className={styles['archived-label']}>
            {' '}
            · Archived {formatDate(project.archivedAt.toString(), 'MMM dd, yyyy')}
            {archivedByMember && ` by ${archivedByMember.fullName}`}
          </span>
        )}
      </td>
      <td style={{ textTransform: 'uppercase' }}>{project.progress}%</td>
      <td>
        {project.usedStorage !== null && project.usedStorage !== undefined
          ? prettyBytes(project.usedStorage)
          : ''}
      </td>
      <td>{project.lastUpdatedAt ? timeDifference(new Date(), project.lastUpdatedAt) : ''}</td>
      <td>{project.dueDate ? formatDate(project.dueDate.toString(), 'MMM dd, yyy') : ''}</td>
      <td>
        <PrioritySelector
          priority={project.priority}
          onChange={(priority) => {
            project.update({ priority });
          }}
          withTitle
        />
      </td>
      {isDesktop && ['owner', 'admin', 'creator'].includes(currentMember?.role || '') && (
        <td>
          <Collaborators
            collaborators={project.collaborators}
            onClick={() =>
              openModal(ProjectCollaboratorModal, {
                project,
              })
            }
          />
        </td>
      )}
      <td />
      <td style={{ paddingRight: '0.8rem', width: '4.8rem' }}>
        <div className={styles['last-cell-wrapper']}>
          <ProjectMenu project={project} />
        </div>
      </td>
    </ListViewItem>
  );
});
